import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowRight, CheckCircle2, FileSignature, Flame, HeartPulse, MessageSquare } from 'lucide-react';
import { signalsApi, Signal } from '@/lib/signalsApi';
import { dealsApi, Deal } from '@/lib/dealsApi';
import { approvalsApi, Approval } from '@/lib/approvals';
import { inboxApi, InboxThread } from '@/lib/inboxApi';

/**
 * Next-best-action card (NUMBER_ONE_PLAN W2.2). The owner's brief: a
 * "data-driven, highly-connected card" that tells the user exactly what to do
 * next. It aggregates the four signals of pending work — approvals blocking
 * teammates, hot un-acted buyer signals, low-health/stale deals, and open
 * conversations — ranks them, and deep-links each into the tool that resolves
 * it. This is the connective tissue that makes the product feel like it's
 * driving, not just storing. Renders nothing until data is present, and a
 * calm "all caught up" state when there is genuinely nothing urgent.
 */

type Suggestion = {
  key: string;
  priority: number;
  icon: typeof Flame;
  tint: string;
  title: string;
  sub: string;
  cta: string;
  to: string;
};

const DAY_MS = 24 * 60 * 60 * 1000;

type TFn = (key: string, opts?: Record<string, unknown>) => string;

/**
 * Pure ranking core — extracted so the priority logic is unit-testable
 * without rendering. Given the four work sources, produces the ranked
 * suggestion list (highest priority first). Deterministic given `now`.
 */
export function buildSuggestions(input: {
  approvals: Approval[];
  signals: Signal[];
  deals: Deal[];
  threads: InboxThread[];
  base: string;
  now: number;
  t: TFn;
}): Suggestion[] {
  const { approvals, signals, deals, threads, base, now, t } = input;
  const out: Suggestion[] = [];

  // 1. Pending approvals — these block teammates, so they rank highest.
  const pending = approvals.filter((a) => a.status === 'pending');
  if (pending.length > 0) {
    out.push({
      key: 'approvals',
      priority: 100,
      icon: FileSignature,
      tint: 'bg-rose-50 text-rose-600',
      title: t('nba.approvals_title', { count: pending.length }),
      sub: t('nba.approvals_sub'),
      cta: t('nba.approvals_cta'),
      to: `${base}/approvals`,
    });
  }

  // 2. Hottest un-acted signal — the freshest reason to reach out.
  const hot = signals
    .filter((s) => s.status === 'new' || s.status === 'read')
    .sort((a, b) => (b.score ?? 0) - (a.score ?? 0))[0];
  if (hot) {
    const name = String(hot.payload?.title ?? t(`signals.kind_${hot.kind}`));
    const isHot = (hot.score ?? 0) >= 50;
    out.push({
      key: 'signal',
      priority: isHot ? 90 : 55,
      icon: Flame,
      tint: 'bg-amber-50 text-amber-600',
      title: t('nba.signal_title', { name }),
      sub: t('nba.signal_sub', { kind: t(`signals.kind_${hot.kind}`) }),
      cta: t('nba.signal_cta'),
      to: `${base}/feed?focus=${hot.id}`,
    });
  }

  // 3. Deal that needs attention — low health first, then staleness.
  const open = deals.filter((d) => !d.closedAt);
  const lowHealth = open
    .filter((d) => typeof d.healthScore === 'number' && d.healthScore < 5)
    .sort((a, b) => (a.healthScore ?? 0) - (b.healthScore ?? 0))[0];
  const stale = open
    .filter((d) => now - new Date(d.updatedAt).getTime() > 14 * DAY_MS)
    .sort((a, b) => new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime())[0];
  const deal = lowHealth ?? stale;
  if (deal) {
    const useHealth = !!lowHealth;
    const days = Math.floor((now - new Date(deal.updatedAt).getTime()) / DAY_MS);
    out.push({
      key: 'deal',
      priority: useHealth ? 80 : 60,
      icon: HeartPulse,
      tint: 'bg-sky-50 text-sky-600',
      title: t('nba.deal_title', { name: deal.name }),
      sub: useHealth
        ? t('nba.deal_sub_health', { score: deal.healthScore })
        : t('nba.deal_sub_stale', { days }),
      cta: t('nba.deal_cta'),
      to: `${base}/deals?focus=${deal.id}`,
    });
  }

  // 4. Most recent open conversation — keep the dialogue moving.
  const thread = threads
    .filter((th) => th.lastMessageAt)
    .sort(
      (a, b) =>
        new Date(b.lastMessageAt as string).getTime() -
        new Date(a.lastMessageAt as string).getTime(),
    )[0];
  if (thread) {
    out.push({
      key: 'reply',
      priority: 50,
      icon: MessageSquare,
      tint: 'bg-purple-50 text-purple-600',
      title: t('nba.reply_title', {
        subject: thread.subject || t('inbox.no_subject', { defaultValue: 'your conversation' }),
      }),
      sub: t('nba.reply_sub'),
      cta: t('nba.reply_cta'),
      to: `${base}/inbox`,
    });
  }

  return out.sort((a, b) => b.priority - a.priority);
}

export function NextBestAction({ wsId }: { wsId: string }) {
  const { t } = useTranslation();

  const signals = useQuery({
    queryKey: ['signals', wsId],
    queryFn: () => signalsApi.list({ workspaceId: wsId, limit: 100 }),
    enabled: !!wsId,
  });
  const deals = useQuery({
    queryKey: ['deals', wsId],
    queryFn: () => dealsApi.list({ workspaceId: wsId, limit: 200 }),
    enabled: !!wsId,
  });
  const approvals = useQuery({
    queryKey: ['approvals', wsId],
    queryFn: () => approvalsApi.list(wsId),
    enabled: !!wsId,
  });
  const threads = useQuery({
    queryKey: ['inbox-threads', wsId, ''],
    queryFn: () => inboxApi.listThreads({ workspaceId: wsId, limit: 50 }),
    enabled: !!wsId,
  });

  const base = `/app/workspace/${wsId}`;

  const suggestions = useMemo<Suggestion[]>(
    () =>
      buildSuggestions({
        approvals: approvals.data ?? [],
        signals: signals.data?.items ?? [],
        deals: deals.data?.items ?? [],
        threads: threads.data?.items ?? [],
        base,
        now: Date.now(),
        t,
      }),
    [approvals.data, signals.data, deals.data, threads.data, base, t],
  );

  const anyLoading =
    signals.isLoading || deals.isLoading || approvals.isLoading || threads.isLoading;
  const anySettled =
    signals.isSuccess || deals.isSuccess || approvals.isSuccess || threads.isSuccess;

  // Hold the slot until at least one source resolves, to avoid a flash.
  if (anyLoading && !anySettled) return null;

  const [primary, ...rest] = suggestions;
  const secondary = rest.slice(0, 2);

  return (
    <div className="mb-6 rounded-2xl border border-ink-200 bg-gradient-to-br from-white to-ink-50/60 p-5">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[11px] uppercase tracking-[0.16em] text-ink-500 font-semibold">
          {t('nba.heading')}
        </span>
      </div>

      {!primary ? (
        <div className="flex items-start gap-3">
          <span className="mt-0.5 rounded-lg bg-emerald-50 text-emerald-600 p-2">
            <CheckCircle2 className="h-5 w-5" />
          </span>
          <div>
            <p className="text-sm font-semibold text-ink-900">{t('nba.caught_up_title')}</p>
            <p className="text-sm text-ink-500 mt-0.5">{t('nba.caught_up_sub')}</p>
          </div>
        </div>
      ) : (
        <>
          <Link
            to={primary.to}
            className="group flex items-center gap-4 rounded-xl bg-white border border-ink-200 px-4 py-3.5 hover:border-accent-300 hover:shadow-sm transition"
          >
            <span className={`rounded-lg p-2.5 ${primary.tint}`}>
              <primary.icon className="h-5 w-5" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-ink-900 truncate">{primary.title}</p>
              <p className="text-sm text-ink-500 truncate">{primary.sub}</p>
            </div>
            <span className="inline-flex items-center gap-1 shrink-0 text-sm font-semibold text-accent-700">
              {primary.cta}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </span>
          </Link>

          {secondary.length > 0 && (
            <div className="mt-3">
              <p className="text-[11px] uppercase tracking-[0.16em] text-ink-400 font-semibold mb-2">
                {t('nba.secondary_heading')}
              </p>
              <div className="grid gap-2 sm:grid-cols-2">
                {secondary.map((s) => (
                  <Link
                    key={s.key}
                    to={s.to}
                    className="group flex items-center gap-3 rounded-lg border border-ink-100 px-3 py-2.5 hover:border-accent-200 hover:bg-white transition"
                  >
                    <span className={`rounded-md p-1.5 ${s.tint}`}>
                      <s.icon className="h-4 w-4" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-[13px] font-medium text-ink-900 truncate">{s.title}</p>
                      <p className="text-xs text-ink-500 truncate">{s.cta}</p>
                    </div>
                    <ArrowRight className="h-3.5 w-3.5 text-ink-400 shrink-0 transition-transform group-hover:translate-x-0.5" />
                  </Link>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
